// CSV export helper — generates a UTF-8 CSV file in the browser

type CsvColumn<T> = {
  key: keyof T | string;
  label: string;
  format?: (val: unknown, row: T) => string;
};

function escapeCsvValue(v: unknown): string {
  if (v === null || v === undefined) {
    return "";
  }
  if (typeof v === "object") {
    try {
      v = JSON.stringify(v);
    } catch {
      v = String(v);
    }
  }
  const s = String(v).replace(/"/g, '""');
  return /[",\n\r]/.test(s) ? `"${s}"` : s;
}

function humanizeKey(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, char => char.toUpperCase());
}

/** Build CSV columns from every key present across the API response rows. */
export function columnsFromApiRows<T extends Record<string, unknown>>(
  rows: T[]
): CsvColumn<T>[] {
  const keys = new Set<string>();
  for (const row of rows) {
    if (!row || typeof row !== "object") {
      continue;
    }
    for (const key of Object.keys(row)) {
      keys.add(key);
    }
  }
  return Array.from(keys).map(key => ({
    key,
    label: humanizeKey(key),
  }));
}

export function exportToCsv<T extends Record<string, any>>(
  filename: string,
  rows: T[],
  columns?: CsvColumn<T>[]
) {
  const cols =
    columns && columns.length > 0
      ? columns
      : columnsFromApiRows(rows as Record<string, unknown>[]);

  const header = cols.map(c => escapeCsvValue(c.label)).join(",");
  const body = rows
    .map(r =>
      cols
        .map(c => {
          const raw = (r as Record<string, unknown>)[c.key as string];
          return escapeCsvValue(c.format ? c.format(raw, r) : raw);
        })
        .join(",")
    )
    .join("\n");
  const csv = "\ufeff" + header + "\n" + body;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Fetch-friendly helper: export every field from API response rows. */
export function exportApiRowsToCsv(
  filename: string,
  rows: Record<string, unknown>[]
) {
  exportToCsv(filename, rows);
}
